<!-- ShareReport.vue: opens, writes the share message right away (streamed), then lets the user edit and post it -->
<template>
  <div class="share-report">
    <h3>{{ $t('report.share.title') }}</h3>
    <p class="lead">{{ $t('report.share.lead') }}</p>

    <div class="language-select-container">
      <label for="language-select">{{ $t('report.share.language') }}</label>
      <select id="language-select" v-model="languageCode" class="language-select" @change="generate">
        <option v-for="lang in supportedLanguages" :value="lang.code" :key="lang.code">{{ $t('report.share.languages.' + lang.code) }}</option>
      </select>
    </div>

    <div class="message-box" :class="{ writing: busy }">
      <textarea
        class="comment"
        ref="messageTextarea"
        rows="6"
        :aria-label="$t('report.share.reportAria')"
        :placeholder="$t('report.share.writing')"
        v-model="text"
        :readonly="busy"
        :aria-busy="busy"
      ></textarea>
      <div class="meta-row">
        <span v-if="busy" class="writing-label"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> {{ $t('report.share.writing') }}</span>
        <span v-else-if="error" class="error-label" role="alert">{{ error }}</span>
        <span v-else class="counter" :class="{ over: remaining < 0 }">{{ remaining }}</span>
        <button v-if="!busy" type="button" class="link-btn" @click="generate">
          <i class="fas fa-rotate" aria-hidden="true"></i> {{ error ? $t('report.share.retry') : $t('report.share.regenerate') }}
        </button>
      </div>
      <p class="ai-note" data-ai-generated="true"><i class="fas fa-robot" aria-hidden="true"></i> {{ $t('report.share.aiNote') }}</p>
    </div>

    <div class="share-options">
      <button class="btn-standard primary" :disabled="busy || !text.trim()" @click="postToX">
        <i class="fab fa-x-twitter" aria-hidden="true"></i> {{ $t('report.share.postX') }}
      </button>
      <div class="secondary-row">
        <button v-if="canShare" class="btn-standard" :disabled="busy || !text.trim()" @click="nativeShare">
          <i class="fas fa-share-nodes" aria-hidden="true"></i> {{ $t('report.share.share') }}
        </button>
        <button class="btn-standard" :disabled="busy || !text.trim()" @click="copyText">
          <i class="fas fa-copy" aria-hidden="true"></i> {{ $t('report.share.copyText') }}
        </button>
        <button class="btn-standard" @click="copyLink">
          <i class="fas fa-link" aria-hidden="true"></i> {{ $t('report.share.copyLink') }}
        </button>
      </div>
    </div>

    <p v-if="status" class="status" role="status">{{ status }}</p>
  </div>
</template>

<script>
const LINK_LENGTH = 23; // X counts every link as 23 characters
const TYPE_INTERVAL_MS = 18;

export default {
  name: "ShareReport",
  props: { submission: { type: Object, required: true } },
  data() {
    return {
      text: "",
      busy: false,
      error: "",
      status: "",
      languageCode: "en",
      supportedLanguages: [
        { code: "en", name: "English" },
        { code: "de", name: "German" },
        { code: "nl", name: "Dutch" },
        { code: "es", name: "Spanish" },
        { code: "it", name: "Italian" },
        { code: "fr", name: "French" },
        { code: "pt", name: "Portuguese" },
        { code: "pl", name: "Polish" },
        { code: "cs", name: "Czech" },
        { code: "sv", name: "Swedish" },
        { code: "fi", name: "Finnish" },
      ],
      run: 0, // identifies the current generation so older streams stop writing
      timer: null,
    };
  },
  computed: {
    link() {
      return `${window.location.origin}/${this.$i18n.locale}/view/${this.submission.submission_id}`;
    },
    remaining() {
      return 280 - LINK_LENGTH - 1 - [...this.text].length;
    },
    canShare() {
      return typeof navigator !== "undefined" && !!navigator.share;
    },
    fullText() {
      return `${this.text.trim()}\n${this.link}`;
    },
    isPraise() {
      return Number(this.submission.severity_level) === 1;
    },
  },
  mounted() {
    const known = (code) => this.supportedLanguages.some((l) => l.code === code);
    const prefix = String(this.submission.submission_id || "").toLowerCase().substring(0, 2);
    this.languageCode = known(this.$i18n.locale) ? this.$i18n.locale : known(prefix) ? prefix : "en";
    this.generate();
  },
  beforeUnmount() {
    this.run++;
    clearInterval(this.timer);
  },
  methods: {
    situation() {
      const s = this.submission;
      const advice = Array.isArray(s.advice_array) && s.advice_array[0] && s.advice_array[0].advice ? s.advice_array[0].advice : "";
      const place = [s.city, s.region, s.country].filter(Boolean).join(", ");
      return [s.comment, advice, place && `Location: ${place}`].filter(Boolean).join(" | ");
    },
    async generate() {
      const myRun = ++this.run;
      clearInterval(this.timer);
      this.busy = true;
      this.error = "";
      this.status = "";
      this.text = "";

      // The server streams the text faster than it reads well: type it out smoothly.
      let full = "";
      let shown = 0;
      let finished = false;
      const settle = () => {
        if (myRun !== this.run) return;
        clearInterval(this.timer);
        this.busy = false;
      };
      this.timer = setInterval(() => {
        if (myRun !== this.run) return clearInterval(this.timer);
        if (shown < full.length) {
          const step = Math.max(1, Math.ceil((full.length - shown) / 40));
          shown += step;
          this.text = full.slice(0, shown);
        } else if (finished) {
          settle();
        }
      }, TYPE_INTERVAL_MS);

      try {
        const language = (this.supportedLanguages.find((l) => l.code === this.languageCode) || {}).name || "English";
        const response = await fetch(`${process.env.VUE_APP_SERVER_URL}/create-tweet-stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt: this.situation(), language, praise: this.isPraise }),
        });
        if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let failed = false;
        for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
          const value = chunk.value;
          if (myRun !== this.run) return;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop();
          for (const part of parts) {
            const line = part.split("\n").find((l) => l.startsWith("data:"));
            if (!line) continue;
            let event;
            try { event = JSON.parse(line.slice(5).trim()); } catch (e) { continue; }
            if (event.type === "delta") full += event.text;
            else if (event.type === "done") full = event.tweet || full;
            else if (event.type === "error") failed = true;
          }
        }
        if (failed || !full.trim()) throw new Error("stream failed");
        finished = true;
      } catch (e) {
        if (myRun !== this.run) return;
        console.error("Share message failed:", e);
        clearInterval(this.timer);
        this.busy = false;
        this.error = this.$t("report.share.errTweet");
      }
    },
    postToX() {
      window.open(`https://x.com/intent/post?text=${encodeURIComponent(this.fullText)}`, "_blank", "noopener");
    },
    async nativeShare() {
      try {
        await navigator.share({ text: this.text.trim(), url: this.link });
      } catch (e) { /* cancelled */ }
    },
    async copyText() {
      try {
        await navigator.clipboard.writeText(this.fullText);
        this.status = this.$t("report.share.copiedReport");
      } catch (e) {
        this.status = this.$t("report.share.errCopyReport");
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.link);
        this.status = this.$t("report.share.copiedLink");
      } catch (e) {
        this.status = this.$t("report.share.errCopyLink");
      }
    },
  },
};
</script>

<style scoped>
.share-report {
  background-color: var(--color-surface);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  gap: 16px;
  flex-direction: column;
  text-align: start;
}
.lead { margin: 0; color: var(--color-secondary); font-size: 14px; }
.language-select-container { display: flex; align-items: center; gap: 8px; }
.language-select {
  padding: 8px 12px;
  font-size: 16px;
  border-radius: 12px;
  border: 1px solid var(--color-border-input);
  background-color: var(--color-surface);
  color: var(--color-text);
  outline: none;
  transition: border-color 0.3s;
  width: 200px;
}
.language-select:focus { border-color: var(--color-primary); }
.message-box textarea { width: 100%; box-sizing: border-box; resize: vertical; }
.message-box.writing textarea { border-color: var(--color-primary); }
.meta-row { display: flex; justify-content: space-between; align-items: center; margin-top: 6px; font-size: 13px; min-height: 24px; }
.counter { color: var(--color-secondary); }
.counter.over { color: var(--color-danger, #c0392b); font-weight: 600; }
.writing-label { color: var(--color-primary); }
.error-label { color: var(--color-danger, #c0392b); }
.link-btn { background: none; border: 0; color: var(--color-primary); cursor: pointer; font-size: 13px; padding: 4px; }
.ai-note { margin: 8px 0 0; font-size: 13px; color: var(--color-secondary); }
.share-options { display: flex; flex-direction: column; gap: 12px; }
.secondary-row { display: flex; flex-wrap: wrap; gap: 8px; }
.secondary-row .btn-standard { flex: 1 1 auto; }
.status { margin: 0; font-size: 14px; color: var(--color-secondary); }
</style>
